import React, { FC, RefObject, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormBaseType, FormState } from "@green0meter/api-client";

import FormBasicsDialog from "../CollectData/components/FormBasicsDialog";
import VsmeGuide from "./VsmeGuide";

import { useGetAvailableForms } from "@api";
import CardGuide from "@components/common/CardGuide/CardGuide";
import { useUserStore } from "@store/user";

interface VsmeGuideContainer {
  refs?: {
    reportsRef?: RefObject<HTMLDivElement>;
    uploadsRef?: RefObject<HTMLDivElement>;
  };
}

const VsmeGuideContainer: FC<VsmeGuideContainer> = ({ refs }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { availableForms } = useGetAvailableForms(user, [FormBaseType.VSME]);

  const enabledForms = useMemo(
    () =>
      availableForms?.filter((form) => form.state === FormState.InProgress) ?? [],
    [availableForms],
  );

  const [newFormDialogOpen, setNewFormDialogOpen] = useState(false);

  const handleNewFormDialogClose = (): void => {
    setNewFormDialogOpen(false);
  };

  const steps: CardGuide["steps"] = [
    {
      title: t("common.stepNumber", { step: 1 }),
      description: t("vsme.guide.steps.dataCollection"),
      detail: {
        title: t("vsme.hub.guidanceTitle"),
        points: [
          t("vsme.hub.assignQuestions"),
          t("vsme.hub.respondentsResponsible"),
          t("vsme.hub.ensureAccuracy"),
          t("vsme.hub.attachDocuments"),
          t("vsme.hub.submitButton"),
          t("vsme.hub.waitConfirmation"),
        ],
      },
      button: {
        text: t("common.openAction"),
        onClick: (): void => {
          if (enabledForms.length > 0) {
            const urlForFirstEnabledForm = `/vsme/form/${enabledForms[0].id}`;

            return navigate(urlForFirstEnabledForm);
          }

          return setNewFormDialogOpen(true);
        },
      },
    },
    {
      title: t("common.stepNumber", { step: 2 }),
      description: t("vsme.guide.steps.analysis"),
      detail: {
        title: t("vsme.hub.guidanceTitle"),
        points: [
          t("vsmehub.step2.p1"),
          t("vsmehub.step2.p2"),
          t("vsmehub.step2.p3"),
          t("vsmehub.step2.p4"),
          t("vsmehub.step2.p5"),
        ],
      },
      button: {
        text: t("common.openAction"),
        onClick: (): void => navigate("/vsme/dashboard"),
      },
    },
    {
      title: t("common.stepNumber", { step: 3 }),
      description: t("vsme.guide.steps.reportGeneration"),
      detail: {
        title: t("vsme.hub.guidanceTitle"),
        points: [
          t("vsmehub.step3.p1"),
          t("vsmehub.step3.p2"),
          t("vsmehub.step3.p3"),
          t("vsmehub.step3.p4"),
        ],
      },
      button: {
        text: t("common.openAction"),
        onClick: (): void =>
          refs?.reportsRef?.current?.scrollIntoView({
            behavior: "smooth",
          }),
      },
    },
    {
      title: t("common.stepNumber", { step: 4 }),
      description: t("vsme.guide.steps.audit"),
      detail: {
        title: t("vsme.hub.guidanceTitle"),
        points: [
          t("vsmehub.step4.p1"),
          t("vsmehub.step4.p2"),
          t("vsmehub.step4.p3"),
          t("vsmehub.step4.p4"),
        ],
      },
      button: {
        text: t("common.openAction"),
        onClick: (): void =>
          refs?.uploadsRef?.current?.scrollIntoView({
            behavior: "smooth",
          }),
      },
    },
  ];

  return (
    <>
      <FormBasicsDialog
        open={newFormDialogOpen}
        formBaseType={FormBaseType.VSME}
        onClose={handleNewFormDialogClose}
      />
      <VsmeGuide steps={steps} />
    </>
  );
};

export default VsmeGuideContainer;
